/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import {
  Button,
  ButtonGroup,
  Chip,
  ChipGroup,
  Dialog,
  InlineNotification,
  LoadingSection,
  Typography2,
} from '@coursera/cds-core';

import { CSV_TEMPLATE_URL_V2 } from 'bundles/enterprise-admin-constants/ProgramMembersConstants';
import GroupAssignmentStep from 'bundles/enterprise-admin-learners/components/invite-learners/GroupAssignmentStep';
import { useCsvInvitePipeline } from 'bundles/enterprise-admin-learners/hooks/useCsvInvitePipeline';
import { FileUploadZone } from 'bundles/enterprise-ui/components/FileUploadZone';

import _t from 'i18n!nls/enterprise-admin-learners';

const { HeadingGroup, Content, Actions } = Dialog;

const styles = {
  content: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-200);
  `,
  uploadSection: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-100);
  `,
  templateNotification: css`
    margin: var(--cds-spacing-100) 0;
  `,
};

export type CsvInviteModalProps = {
  open: boolean;
  onClose: () => void;
  onBack: () => void;
  /**
   * Set when the group is already known (Group Details lock, single-group admin).
   * // TODO(ENTADMIN-2082): once CSV submission is implemented, this will be used to
   * auto-select the group in GroupAssignmentStep and skip the group step entirely.
   */
  autoSelectedGroupId?: string;
};

/**
 * CSV bulk-invite flow: template download, upload against the existing
 * presigned-URL flow, and a preview of the uploaded file via `previewImportAttributes`. A
 * successful preview hands off to GroupAssignmentStep.
 *
 * TODO(ENTADMIN-2082): Add ProcessImportSync/Async flows.
 */
const CsvInviteModal = ({ open, onClose, onBack, autoSelectedGroupId }: CsvInviteModalProps) => {
  const pipeline = useCsvInvitePipeline({ autoSelectedGroupId });
  const { reset, file } = pipeline;
  const [isUploadNotificationDismissed, setIsUploadNotificationDismissed] = React.useState(false);
  const [csvSubmitError, setCsvSubmitError] = React.useState('');
  const isAddToGroupFlow = Boolean(autoSelectedGroupId);
  const title = isAddToGroupFlow ? _t('Add learners using CSV') : _t('Invite learners using CSV');
  const processingLabel = isAddToGroupFlow ? _t('Adding your learners…') : _t('Processing your invites…');

  // TODO(ENTADMIN-2082): replace with the real ProcessImportSync/ProcessImportAsync call, roster
  // resolution, and outcome/async-modal handling. This ticket only wires the group-picker seam.
  const handleCsvSubmit = async (): Promise<void> => {
    try {
      throw new Error('To be added');
    } catch {
      setCsvSubmitError(_t('There was an error processing your CSV. Please try again.'));
    }
  };

  const handleGroupStepBack = () => {
    setCsvSubmitError('');
    pipeline.reset();
  };

  React.useEffect(() => {
    if (!open) reset();
  }, [open, reset]);

  React.useEffect(() => {
    if (file) setIsUploadNotificationDismissed(false);
  }, [file]);

  if (pipeline.status === 'processing') {
    return (
      <Dialog open={open} onClose={() => {}} width="large">
        <HeadingGroup>{title}</HeadingGroup>
        <Content>
          <LoadingSection label={processingLabel} />
        </Content>
        <Actions />
      </Dialog>
    );
  }

  if (pipeline.status === 'success') {
    return (
      <GroupAssignmentStep
        open={open}
        onBack={handleGroupStepBack}
        onSubmit={handleCsvSubmit}
        submitting={false}
        errorMessage={csvSubmitError || undefined}
      />
    );
  }

  return (
    <Dialog open={open} onClose={onClose} width="large">
      <HeadingGroup supportText={_t('Upload a CSV with names, emails, and optional group or attribute details.')}>
        {title}
      </HeadingGroup>
      <Content css={styles.content}>
        {/* Stable live region — keep mounted so content changes get announced. */}
        <div role="status">
          {file && !isUploadNotificationDismissed && (
            <InlineNotification severity="success" onDismiss={() => setIsUploadNotificationDismissed(true)}>
              {_t('Learner data uploaded successfully.')}
            </InlineNotification>
          )}
        </div>

        <InlineNotification
          css={styles.templateNotification}
          severity="information"
          title={_t('Download the CSV template')}
          inlineAction={
            <Button variant="ghost" component="a" href={CSV_TEMPLATE_URL_V2} target="_blank" rel="noopener noreferrer">
              {_t('Download')}
            </Button>
          }
        >
          {_t('Name and email required. Add groups or custom attributes to organise learners.')}
        </InlineNotification>

        <div css={styles.uploadSection}>
          <Typography2 component="p" variant="subtitleSmall">
            {_t('Upload the CSV')}
          </Typography2>

          <FileUploadZone
            layout="stacked"
            accept={{ 'text/csv': ['.csv'] }}
            singleFileOnly
            defaultMessage={_t('Click to upload or drag & drop')}
            defaultFileMessage={_t('CSV file only')}
            onDrop={({ files }) => {
              const [nextFile] = files;
              if (nextFile) pipeline.selectFile(nextFile);
            }}
          />

          {file && (
            <ChipGroup aria-label={_t('Uploaded file')} variant="delete">
              <Chip onClick={pipeline.clearFile}>{file.name}</Chip>
            </ChipGroup>
          )}
        </div>

        {/* Stable live region — keep mounted so content changes get announced. */}
        <div role="alert">
          {pipeline.status === 'error' && <InlineNotification severity="error">{pipeline.error}</InlineNotification>}
        </div>
      </Content>
      <Actions>
        <ButtonGroup
          primaryButtonProps={{
            children: isAddToGroupFlow ? _t('Add to group') : _t('Upload and invite'),
            disabled: !pipeline.file || !pipeline.programReady,
            onClick: pipeline.submit,
          }}
          alternateButtonProps={{
            variant: 'secondary',
            children: _t('Back'),
            onClick: onBack,
          }}
        />
      </Actions>
    </Dialog>
  );
};

export default CsvInviteModal;
